<!-- =========================================================================================
  File Name: DashboardAnalytics.vue
  Description: Dashboard Analytics

========================================================================================== -->

<template>
  <div id="course-page">
    <vs-alert :active.sync="errorOccurred" color="danger" title="Simulation Not Found">
      <span>Course record with id: {{ $route.params.courseId }} not found. </span>
      <span>
        <span>Check </span><router-link :to="{name:'course-selector'}"
                                        class="text-inherit underline">Course Selector</router-link>
      </span>
    </vs-alert>

    <div class="vx-row">
      <div class="vx-col w-full mb-base">
        <PuSkeletonTheme :duration="0.1" color="rgb(163,163,163, 0.75)" highlight="rgb(211,211,211)">

          <div class="knowledge-base-jumbotron bg-cover rounded-lg flex w-full p-8 lg:p-12"
               style="background-image: url(https://images.unsplash.com/photo-1620121692029-d088224ddc74?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=2089&q=80)">
            <div class="flex justify-between w-full">
              <div class="flex flex-wrap content-center pl-6 pr-6">
                <h1 class="mb-2 text-white w-full" style="font-size: 4rem" v-if="courseData">{{ courseData.name }}</h1>
                <PuSkeleton v-else class="my-2 w-3/5 h-4"></PuSkeleton>
                <p
                  v-if="courseCompletionData.completion_percent < 50"
                  class="my-2 xl:w-3/4 lg:w-4/5 md:w-2/3 w-4/5 text-white">
                  Looks like your just getting started, Lets try to reach 50% by the end of the week!</p>
                <p
                  v-else-if="courseCompletionData.completion_percent < 80"
                  class="my-2 xl:w-3/4 lg:w-4/5 md:w-2/3 w-4/5 text-white">
                  Your more then half way done, Lets complete this course!</p>
                <p
                  v-else-if="courseCompletionData.completion_percent === 100"
                  class="my-2 xl:w-3/4 lg:w-4/5 md:w-2/3 w-4/5 text-white">
                  You have completed this course, Great job!</p>
                <p v-else-if="courseCompletionData.completion_percent">Looks like you haven't
                  practiced this course
                  yet.</p>
                <PuSkeleton v-else class="my-2 w-4/5 h-4"></PuSkeleton>
                <vs-progress
                  :percent="courseCompletionData.completion_percent"
                  style="max-width: 600px"
                  class="block mt-6 shadow-md self-center w-2/3" color="success"></vs-progress>
              </div>
              <img
                :src="courseData.thumbnail !== null ? courseData.thumbnail : 'https://cdn.discordapp.com/attachments/746242308084858900/848762736255172679/default-placeholder.png'"
                class="rounded hidden lg:block" style="max-width: 275px; max-height: 275px; width: 20%;">
            </div>
          </div>
        </PuSkeletonTheme>
      </div>

    </div>

    <div class="vx-row match-height">
      <div class="vx-col w-full mb-base lg:w-1/3 ">
        <vx-card title="Course Simulation Progress">
          <div v-if="courseSimulationChart" slot="no-body">
            <vue-apex-charts :options="courseProgressCardConfig.totalSimulationsPie.chartOptions"
                             :series="courseSimulationChart.series" class="mt-10 mb-10"
                             height="345"
                             type="pie"/>

            <ul class="mb-1">
              <li v-for="customerData in courseSimulationChart.data" :key="customerData.name"
                  class="flex justify-between py-3 px-6 border d-theme-border-grey-light border-solid border-r-0 border-l-0 border-b-0">
                                <span class="flex items-center">
                                    <span :class="`bg-${customerData.color}`"
                                          class="inline-block h-3 w-3 rounded-full mr-2"></span>
                                    <span class="font-semibold">{{ customerData.name }}</span>
                                </span>
                <span>{{ customerData.counts }}</span>
              </li>
            </ul>
          </div>
          <PuSkeletonTheme v-else :duration="0.1" :color="skeletonColor" :highlight="skeletonHighlight">
            <div class="mb-8">
              <div class="flex justify-center">
                <PuSkeleton class="block shadow-md w-full" style="height: 270px;">
                </PuSkeleton>
              </div>
            </div>
            <div class="flex justify-between flex-wrap">
              <PuSkeleton class="mt-4 w-full" style="width: 100%; height: 35px;">
              </PuSkeleton>
            </div>
            <div class="flex justify-between flex-wrap">
              <PuSkeleton class="mt-4 w-full" style="width: 100%; height: 35px;">
              </PuSkeleton>
            </div>
            <div class="flex justify-between flex-wrap">
              <PuSkeleton class="mt-4 w-full" style="width: 100%; height: 35px;">
              </PuSkeleton>
            </div>
          </PuSkeletonTheme>
        </vx-card>
      </div>

      <div class="vx-col w-full mb-base lg:w-2/3 ">
        <vx-card title="Single Simulation Progress">
          <vs-table :data="courseSimulationTable" max-items="6" pagination v-if="courseSimulationTable" search>

            <template slot="thead">
              <vs-th>Name</vs-th>
              <vs-th>Progress</vs-th>
            </template>

            <template slot-scope="{data}">
              <vs-tr v-for="(attach) in data" :key="attach.id">

                <vs-td :data="`${attach.name}`">
                  <div class="flex align-middle items-center">
                    <img :src="attach.thumbnail" class="rounded mr-3" width="40px">
                    {{ `${attach.name}` }}
                  </div>
                </vs-td>

                <vs-td>
                  <div v-if="attach.progress === 'completed'" class="flex items-center justify-center">
                    <feather-icon icon="CheckCircleIcon" svgClasses="h-6 w-6 text-success" class="mr-2" />
                    <h6>Completed</h6>
                  </div>
                  <div v-else-if="attach.progress === 'started'" class="flex items-center justify-center">
                    <feather-icon icon="ClockIcon" svgClasses="h-6 w-6 text-warning" class="mr-2" />
                    <h6>Started</h6>
                  </div>
                  <div v-else class="flex items-center justify-center">
                    <feather-icon icon="XCircleIcon" svgClasses="h-6 w-6 text-danger" class="mr-2" />
                    <h6>Not Started</h6>
                  </div>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </vx-card>
      </div>


      <!---
      <div class="vx-col w-full lg:w-2/3 ">
        <vx-card title="Practiced Simulations Per Day">
          <vue-apex-charts v-if="simulation_chart_series"
                           :options="dashboardCardConfig.simulationsLineChartDaily.chartOptions"
                           :series="simulation_chart_series" height="450"
                           type="area"></vue-apex-charts>
        </vx-card>
      </div>
      ---->
    </div>

    <div class="vx-row">
      <div class="vx-col w-full">
        <vx-card>
          <h1>Course Completion</h1>
          <div class="flex justify-center items-center">
            <div style="min-height: 150px" class="mr-2">
              <lottie-animation
                path="https://sims-cdn.intellectualpoint.com/lottie/48055-working-online.json"
                :loop="true"
                :autoPlay="true"
                :speed="1"
                :width="256"
              />
            </div>
            <vs-progress class="mr-4 ml-2" :height="12" :percent="courseCompletionData.completion_percent" color="success"></vs-progress>
            <h1 class="ml-4 mr-4" style="font-size: 3.2rem">{{ courseCompletionData.completion_percent ? courseCompletionData.completion_percent : 0}}%</h1>
          </div>
        </vx-card>
      </div>
    </div>

    <!---
    <div class="vx-row">
      <div class="vx-col w-full mb-base">
        <vx-card title="Practiced Time Per Day">
          <vue-apex-charts v-if="time_chart_series" :options="dashboardCardConfig.timeLineChartDaily.chartOptions"
                           :series="time_chart_series" height="450"
                           type="area"></vue-apex-charts>
        </vx-card>
      </div>
    </div>
    --->

  </div>
</template>

<script>
import ListRecentSimulations from '@/views/course/course-view/recent-simulations/ListRecentSimulations'
import VueApexCharts from 'vue-apexcharts'
import StatisticsCardLine from '@/components/statistics-cards/StatisticsCardLine.vue'
import DashboardStatisticsCardLine from '@/components/statistics-cards/DashboardStatisticsCardLine.vue'
import courseProgressCardConfig from '@/components/card-config/courseProgressCardConfig'
import LottieAnimation from '@/components/lottie/LottieAnimation'

export default {
  data () {
    return {
      courseData: null,
      errorOccurred: false,

      courseCompletionData: {},
      courseSimulationChart: null,
      courseSimulationTable: [],

      courseProgressCardConfig
    }
  },
  components: {
    VueApexCharts,
    StatisticsCardLine,
    DashboardStatisticsCardLine,
    ListRecentSimulations,
    LottieAnimation
  },
  computed: {
    activeCourseInfo () {
      return this.$store.state.AppActiveCourse
    },
    skeletonColor () {
      const theme = this.$store.state.theme
      if (theme === 'light') return 'rgb(224, 224, 224)'
      if (theme === 'midnight') return 'rgb(5, 0, 53, 1)'
      if (theme === 'dark') return 'rgb(22, 23, 25)'
    },
    skeletonHighlight () {
      const theme = this.$store.state.theme
      if (theme === 'light') return 'rgb(248, 248, 248)'
      if (theme === 'midnight') return 'rgb(27 25 53)'
      if (theme === 'dark') return 'rgb(94 94 94 / 73%)'
    }
  },
  methods: {

    loadCourseData (id) {
      this.$http.get(`courses/${id}`)
        .then(response => {

          if (response.status === 200) {
            this.courseData = response.data.data
          }

        })
        .catch(error => {
          this.errorOccurred = true
          this.error_msg = error
        })
    },
    loadCourseProgress (id, uid) {

      let timezoneOffset = new Date().getTimezoneOffset()
      timezoneOffset = timezoneOffset === 0 ? 0 : -timezoneOffset

      this.$http.get(`progress/user/${uid}/course-completion?id=${id}&rich=true`)
        .then(response => {
          if (response.data.data) {
            this.courseCompletionData = response.data.data
            this.courseSimulationChart = {
              data: [
                {
                  'name': 'Not Started',
                  'counts': response.data.data.simulations.count.not_started,
                  'color': 'primary'
                },
                {
                  'name': 'Started',
                  'counts': response.data.data.simulations.count.started,
                  'color': 'warning'
                },
                {
                  'name': 'Completed ',
                  'counts': response.data.data.simulations.count.completed,
                  'color': 'success'
                }
              ],
              series: [
                response.data.data.simulations.count.not_started,
                response.data.data.simulations.count.started,
                response.data.data.simulations.count.completed
              ]
            }
            for (const [type, value] of Object.entries(response.data.data.simulations.list)) {
              value.forEach(item => {
                item.progress = type
                this.courseSimulationTable.push(item)
              })
            }
          }
        }).catch(() => {
        })

      /*  this.$http.get(`progress/p/@me/course/completion?id=${id}`)
          .then(response => {

            if (response.data.data) {

              this.course_completion_data = response.data.data.total_course_completion

              this.course_completion_chart = {
                analyticsData: [
                  {
                    'name': 'Not Started',
                    'counts': this.course_completion_data.not ? this.course_completion_data.not : 0,
                    'color': 'primary'
                  },
                  {
                    'name': 'Started',
                    'counts': this.course_completion_data.started ? this.course_completion_data.started : 0,
                    'color': 'warning'
                  },
                  {
                    'name': 'Completed ',
                    'counts': this.course_completion_data.completed ? this.course_completion_data.completed : 0,
                    'color': 'success'
                  }
                ],
                series: [
                  this.course_completion_data.not ? this.course_completion_data.not : 0,
                  this.course_completion_data.started ? this.course_completion_data.started : 0,
                  this.course_completion_data.completed ? this.course_completion_data.completed : 0
                ]
              }

            }

          })
          .catch(() => {
          })

        this.$http.get(`progress/p/@me/course/simulation-chart?id=${id}`)
          .then(response => {

            if (response.data.data) {

              this.simulation_chart_series = [
                {
                  name: 'Started',
                  data: response.data.data.started
                }, {
                  name: 'Completed',
                  data: response.data.data.completed
                }
              ]
            }
          }).catch(() => {
          })

        this.$http.get(`progress/p/@me/course/time-chart?id=${id}&tz=${timezoneOffset}`)
          .then(response => {

            if (response.data.data) {

              this.time_chart_series = [
                {
                  name: 'Time Practiced',
                  data: response.data.data.time
                }
              ]
            }
          }).catch(() => {
          }) */

    }
  },
  beforeDestroy () {
    this.items_local = null
  },
  beforeMount () {
    this.loadCourseData(this.$route.params.courseId)
    this.loadCourseProgress(this.$route.params.courseId, this.$route.params.userId)
  },
  watch: {
    '$route' () {
      this.loadCourseData(this.$route.params.courseId)
      this.loadCourseProgress(this.$route.params.courseId, this.$route.params.userId)
    }
  }
}
</script>
